import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ShopService } from '@services/shops/shop.service';
import { Shop } from '@services/shops/shop.model';
import { Observable, Subject } from 'rxjs';
import {
  CustomUpsellExtension,
  ExtensionComponentType,
  ICheckoutExtensions,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { CheckoutReducer } from '@pages/checkout-app/checkout-store/reducers';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { takeUntil } from 'rxjs/operators';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { ViewMode } from '@pages/checkout-app/checkout-layout/main/components/extensions/models';
import { FieldType } from '@services/settings/settings.service';
import { CheckoutExtensionCommonConfig } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import PlacementMode = CheckoutExtensionCommonConfig.PlacementMode;

import {
  AUTO_ADD_DEFAULTS,
  CustomUpsellConfig,
  TRUST_SIGNALS_DEFAULT_BACKGROUND,
  TRUST_SIGNALS_DEFAULT_BORDER,
  TRUST_SIGNALS_DEFAULT_CORNER_RADIUS,
  UpsellRateTypeConfig,
} from '@app/pages/checkout-app/checkout-layout/main/components/extensions/components/extension-components/custom-upsell/model';
import { CustomUpsellDto } from '@app/pages/checkout-app/checkout-layout/main/components/extensions/components/extension-components/custom-upsell/custom-upsell.dto';
import { CustomUpsellFormComponent } from './components/custom-upsell-form/custom-upsell-form.component';
import { camelCaseToTitleCase, generateRandomString } from '@utils/string-utils';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';

@Component({
  selector: 'custom-upsell',
  templateUrl: './custom-upsell.component.html',
  styleUrls: ['./custom-upsell.component.scss'],
  standalone: false,
})
export class CustomUpsellComponent implements OnInit, OnDestroy {
  @ViewChild(CustomUpsellFormComponent) customUpsellFormComponent: CustomUpsellFormComponent;

  @onDestroyComplete() destroy$ = new Subject();
  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  customUpsellConfig = [...CustomUpsellConfig];
  upsellRateTypeConfig = [...UpsellRateTypeConfig];
  entities;
  readonly formArrayFieldName = 'restrictionsRules';
  productImageBase64;
  formGroup: UntypedFormGroup;
  restrictionsRules = getExtensionRules();
  shop$: Observable<Shop>;
  shopCurrencySymbol: string;
  updateRequired: boolean = false;
  updateAppUrl: string = '';
  PlacementMode = PlacementMode;
  camelCaseToTitleCase = camelCaseToTitleCase;
  applyDiscountToOptions = [
    {
      label: 'Upsell item',
      value: 'upsellItem',
    },
    {
      label: 'Entire order',
      value: 'orderSubtotal',
    },
  ];

  constructor(
    public shopService: ShopService,
    private formBuilder: UntypedFormBuilder,
    private store: Store<fromCheckoutReducer.State>,
    private rulesDynamicFormService: RulesDynamicFormService,
    private extensionsService: ExtensionsService,
  ) {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.CUSTOM_UPSELL);
    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleSave();
    });

    this.shop$ = this.shopService.shop$;
    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;
    this.formGroup = this.formBuilder.group(this.initForm());
    this.attachImprovedDeliveryAssurance();
    this.shopService
      .getUrlToUpdateAppIfRequired(['read_publications', 'write_publications'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (!res) return;
        this.updateRequired = res.requireUpdate;
        this.updateAppUrl = res.updateUrl;
        this.extensionsService.setFormValid(this.formGroup.valid && !this.updateRequired);
      });

    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.formGroup = this.formBuilder.group(
          this.initForm(this.normalizeAutoAdd(extension.template), extension?.usageToken),
        );
        this.attachImprovedDeliveryAssurance(extension.template);
      }
    });

    this.entities = this.restrictionsRules.map((r) => r.entity);
    this.initChangesForExampleView();

    // Report form validity changes
    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid && !this.updateRequired);
    });
    this.extensionsService.setFormValid(this.formGroup.valid && !this.updateRequired);
  }

  ngOnDestroy() {}

  initChangesForExampleView() {
    const exampleTemplate = new CustomUpsellDto().createExampleTemplate();
    this.dispatchExampleViewValues(this.normalizeAutoAdd(this.loadedExtension?.template) ?? exampleTemplate);
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });
  }

  dispatchExampleViewValues(values) {
    if (!values) {
      return;
    }

    let timerConfiguration = this.customUpsellFormComponent?.getTimerConfiguration();

    if (!timerConfiguration && this.loadedExtension?.template?.timerConfiguration) {
      timerConfiguration = this.loadedExtension.template.timerConfiguration;
    }

    const template = {
      title: values.title,
      description: values.description,
      upsellRate: values.upsellRate,
      discount: values.discount,
      productImageBase64: this.productImageBase64,
      markAsChecked: !!values.autoAdd?.enabled,
      tooltipEnabled: values.tooltipEnabled,
      tooltipTitle: values.tooltipTitle,
      tooltipText: values.tooltipText,
      placementMode: values.placementMode,
      applyDiscountTo: values.applyDiscountTo,
      upsellRateType: values.upsellRateType,
      timerConfiguration: timerConfiguration,
      keepInCart: values.keepInCart,
      autoAdd: values.autoAdd,
      trustSignals: values.trustSignals,
      infoPopupImageUrl: values.infoPopupImage,
    };

    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.CUSTOM_UPSELL]: {
        mode,
        template,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  initForm(loadedValues?: any, usageToken?: string | null, fieldsetFields?: any[]) {
    const newValues = {};
    if (fieldsetFields?.length) {
      fieldsetFields.forEach((field) => {
        newValues[field.fieldName] = [
          loadedValues ? loadedValues[field.fieldName] : field.defaultValue,
          field.validators,
        ];
      });

      return newValues;
    }
    this.customUpsellConfig.forEach((field) => {
      if (field.fieldName === 'usageToken') {
        field.defaultValue = 'custom_upsell_' + generateRandomString(12);
        newValues[field.fieldName] = usageToken ? usageToken : field.defaultValue;
      } else if (field.type === 'fieldset') {
        newValues[field.fieldName] = this.formBuilder.group(
          this.initForm(loadedValues ? loadedValues[field.fieldName] : undefined, null, field.fields),
        );
      } else if (field.type == 'dynamic-ruleset') {
        newValues[field.fieldName] = this.rulesDynamicFormService.initRulesFormArray(
          loadedValues && loadedValues[field.fieldName],
          field.rulesConfig,
        );
      } else if (field.fieldName == 'placementMode') {
        newValues[field.fieldName] = [loadedValues?.[field.fieldName] || field.defaultValue, field.validators];
      } else {
        newValues[field.fieldName] = [
          loadedValues ? (loadedValues[field.fieldName] ?? field.defaultValue) : field.defaultValue,
          field.validators,
        ];
      }
    });
    return newValues;
  }

  private attachImprovedDeliveryAssurance(loadedTemplate?: CustomUpsellExtension.Template): void {
    this.formGroup.setControl('trustSignals', this.buildTrustSignalsGroup(loadedTemplate?.trustSignals));
    if (loadedTemplate?.infoPopupImageUrl && !this.formGroup.get('infoPopupImage')?.value) {
      this.formGroup.get('infoPopupImage')?.setValue(loadedTemplate.infoPopupImageUrl);
    }
  }

  private buildTrustSignalsGroup(trustSignals?: CustomUpsellExtension.TrustSignals): UntypedFormGroup {
    const items = (trustSignals?.items ?? []).map((item) => this.buildTrustSignalItem(item));
    return this.formBuilder.group({
      enabled: [!!trustSignals?.enabled],
      layout: [trustSignals?.layout ?? 'vertical'],
      backgroundColor: [trustSignals?.backgroundColor ?? TRUST_SIGNALS_DEFAULT_BACKGROUND],
      border: [trustSignals?.border ?? TRUST_SIGNALS_DEFAULT_BORDER],
      cornerRadius: [trustSignals?.cornerRadius ?? TRUST_SIGNALS_DEFAULT_CORNER_RADIUS],
      items: this.formBuilder.array(items),
    });
  }

  private buildTrustSignalItem(item?: CustomUpsellExtension.TrustSignalItem): UntypedFormGroup {
    return this.formBuilder.group({
      icon: [item?.icon ?? ''],
      richText: [item?.richText ?? ''],
    });
  }

  private normalizeAutoAdd(template: any): any {
    if (!template || template.autoAdd) return template;
    return {
      ...template,
      autoAdd: {
        ...AUTO_ADD_DEFAULTS,
        enabled: !!template.markAsChecked,
      },
    };
  }

  onApplyDiscountToChange = (event) => {
    const control = this.formGroup.get('applyDiscountTo');
    control.setValue(event.value);
    control.markAsDirty();
  };

  handleSave() {
    const template = { ...this.formGroup.value };
    const customUpsellDTO = new CustomUpsellDto();
    template.productImageBase64 = this.productImageBase64;

    const timerConfiguration = this.customUpsellFormComponent?.getTimerConfiguration();
    if (timerConfiguration) {
      template.timerConfiguration = timerConfiguration;
    }

    const customUpsellObject: Partial<ICheckoutExtensions> = {
      template: customUpsellDTO.createCustomUpsellTemplateDTO(template),
      type: ExtensionComponentType.CUSTOM_UPSELL,
      usageToken: template.usageToken ?? null,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: customUpsellObject }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: customUpsellObject }));
    }
  }

  clearImage = () => {
    this.formGroup.get('productImageBase64').setValue('');
    this.onProductImageChange(null);
  };

  onProductImageChange = (base64Image) => {
    this.productImageBase64 = base64Image;
    this.formGroup.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  };

  onUpdateAppPermissions = () => {
    if (this.updateAppUrl) {
      window.open(this.updateAppUrl, '_blank', 'noopener noreferrer');
    }
  };

  handleTimerConfigurationChange = () => {
    this.extensionsService.setFormValid(true);
    this.dispatchExampleViewValues(this.formGroup.value);
  };

  protected readonly FieldType = FieldType;
}
